import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchQuotes, deleteQuote, sendQuote, duplicateQuote, clearSendStatus } from "../../store/slices/quotesSlice";
import { formatShortDate, formatRelativeDate, getStatusColor, formatCurrency } from "../../utils/format";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  EyeIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "sent", label: "Sent" },
  { key: "viewed", label: "Viewed" },
  { key: "accepted", label: "Accepted" },
  { key: "expired", label: "Expired" },
];

export default function QuotesList() {
  const dispatch = useDispatch();
  const { list, totalQuotes, totalPages, currentPage, status, sendStatus } =
    useSelector((state) => state.quotes);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    dispatch(fetchQuotes({ status: activeTab, search, page, limit: 20 }));
  }, [dispatch, activeTab, search, page]);

  useEffect(() => {
    if (sendStatus === "succeeded") {
      setSendingId(null);
      dispatch(clearSendStatus());
      dispatch(fetchQuotes({ status: activeTab, search, page, limit: 20 }));
    }
  }, [sendStatus, dispatch, activeTab, search, page]);

  const handleSend = (id) => {
    if (window.confirm("Send this quote to the client?")) {
      setSendingId(id);
      dispatch(sendQuote(id));
    }
  };

  const handleDuplicate = (id) => {
    dispatch(duplicateQuote(id));
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this quote?")) {
      dispatch(deleteQuote(id));
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quotes</h1>
          <p className="text-gray-500 text-sm mt-1">{totalQuotes} total quotes</p>
        </div>
        <Link
          to="/quotes/new"
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Quote
        </Link>
      </div>

      {/* Search + Status Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Search by client name, email, ship, cruise line..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
        <div className="flex overflow-x-auto px-4">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setPage(1); }}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {status === "loading" ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
            <p className="mt-3 text-sm text-gray-500">Loading quotes...</p>
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center">
            <FunnelIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No quotes found</h3>
            <p className="text-sm text-gray-500 mb-4">
              {search ? "Try adjusting your search" : "Create your first quote to get started"}
            </p>
            {!search && (
              <Link
                to="/quotes/new"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Quote
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cruise</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sail Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {list.map((quote) => {
                    const lowestPrice = Math.min(...quote.cabinSelections.map((c) => c.pricePerPerson));
                    const currency = quote.cabinSelections[0]?.currency || "CAD";

                    return (
                      <tr key={quote._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <Link to={`/quotes/${quote._id}`} className="block">
                            <p className="text-sm font-medium text-gray-900">
                              {quote.client.firstName} {quote.client.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{quote.client.email}</p>
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">{quote.sailing.ship}</p>
                          <p className="text-xs text-gray-500">{quote.sailing.cruiseLine} &bull; {quote.sailing.nights}N</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatShortDate(quote.sailing.sailDate)}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(lowestPrice, currency)}
                          </p>
                          <p className="text-xs text-gray-500">per person</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                            {quote.status}
                          </span>
                          {quote.viewCount > 0 && (
                            <span className="ml-1 text-xs text-gray-400">
                              <EyeIcon className="inline h-3 w-3" /> {quote.viewCount}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatRelativeDate(quote.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {(quote.status === "draft" || quote.status === "viewed") && (
                              <button
                                onClick={() => handleSend(quote._id)}
                                disabled={sendingId === quote._id}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Send to client"
                              >
                                <PaperAirplaneIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDuplicate(quote._id)}
                              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Duplicate"
                            >
                              <DocumentDuplicateIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(quote._id)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
